import type { ReactNode } from "react";

import { cn } from "../cn";
import { Logo } from "./Logo";

/**
 * The ecosystem top bar (`design-system-spec.md` §The frame).
 *
 * Named slots rather than a single `children`, because the bar's **layout** is
 * the design system's — the divider after the brand, the spacer before the
 * search, the 56px height — while the **content** is the shell's: which product
 * is open, which organization is active, who is signed in. That split is what
 * lets `app-shell` hold no markup of its own and still have a face.
 *
 * The earlier version of this component was sticky, translucent and took one
 * `right` slot; nothing used it, and the shell hand-rolled the real bar instead.
 * This is the bar the shell actually needs.
 */
export interface AppBarProps {
  /** Quién firma arriba a la izquierda. Por omisión, Orkestor.
   *
   *  Es una ranura y no un `<Logo>` fijo porque entrar en un producto es entrar
   *  del todo: la barra —su alto, sus separadores, su orden— es del sistema de
   *  diseño, pero la marca es del producto que está abierto, y el sistema de
   *  diseño no puede conocer ninguno (esa es la regla de capas). */
  brand?: ReactNode;
  /** Product switcher — absent on platform screens, where no product is open. */
  product?: ReactNode;
  /** Active organization selector. */
  org?: ReactNode;
  /** Search field. Hidden below `md` by the shell if it wants. */
  search?: ReactNode;
  /** Icon actions: notifications, help… */
  actions?: ReactNode;
  /** The user menu, always last. */
  user?: ReactNode;
  className?: string;
}

export function AppBar({ brand, product, org, search, actions, user, className }: AppBarProps) {
  return (
    <header
      className={cn(
        "flex h-14 flex-none items-center gap-[14px]",
        "border-b border-line bg-surface px-[18px]",
        className,
      )}
    >
      {/* La marca y el selector van pegados: el selector es una flechita de la
          marca, no un control aparte. Lo que dice en qué producto estás es el
          logotipo. */}
      <div className="flex items-center gap-[4px]">
        {brand ?? <Logo className="h-[40px]" />}
        {product}
      </div>
      {org && <span aria-hidden className="h-[22px] w-px bg-line" />}
      {org}
      <div className="flex-1" />
      {search}
      {actions}
      {user}
    </header>
  );
}
